import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { authedFetch, checkSignupEmail, errText } from "../lib/api";
import { VerifyEmailNotice } from "../components/VerifyEmailNotice";
import { AuthLayout } from "../components/AuthLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Gift, PauseCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { DemoVideo } from "../components/DemoVideo";
import { PasswordInput } from "../components/PasswordInput";

export default function ClinicSignup() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ firstName: searchParams.get("first_name") || "", lastName: "", clinicName: searchParams.get("clinic_name") || "", suburb: "", email: searchParams.get("email") || "", password: "", confirmPassword: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [emailConflict, setEmailConflict] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setEmailConflict(false);
    if (form.password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (form.password !== form.confirmPassword) { setError("Passwords do not match"); return; }
    setLoading(true);
    const emailCheck = await checkSignupEmail(form.email);
    if (!emailCheck.ok) {
      setLoading(false);
      setError(emailCheck.message);
      setEmailConflict(emailCheck.code === "EMAIL_IN_USE");
      return;
    }
    const { firstName, lastName, clinicName, suburb, email, password } = form;
    const { data, error: signErr } = await signUp({ firstName, lastName, clinicName, suburb, email, password, role: "clinic" });
    if (signErr) { setLoading(false); const m = errText(signErr, "Sign up failed. Please try again."); setError(/already|registered|exists/i.test(m) ? "This email is already registered. Please sign in instead." : m); toast.error(m); return; }
    if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      setLoading(false);
      setError("This email is already registered. Please sign in instead.");
      return;
    }
    if (!data?.session) {
      setLoading(false);
      setVerifyEmail(form.email);
      return;
    }
    const res = await authedFetch("/onboard/clinic", { method: "POST", body: JSON.stringify({ account_name: form.clinicName }) });
    setLoading(false);
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      const m = errText(d.detail, "Could not set up your clinic. Please try again.");
      setError(m);
      toast.error(m);
      return;
    }
    toast.success("Welcome! Your 3-month free trial has started.");
    navigate("/clinic", { replace: true });
  };

  return (
    verifyEmail ? <VerifyEmailNotice email={verifyEmail} testId="clinic-verify-email" /> :
    <AuthLayout eyebrow="Clinic Manager" title="Register your clinic" subtitle="Manage your clinicians and referrals in one place." testId="clinic-signup-page">
      <div className="mb-5 space-y-2">
        <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-3" data-testid="trial-callout">
          <Gift className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
          <p className="text-sm font-semibold text-emerald-800">3 months free. Plans start from $39/month after trial. No card required.</p>
        </div>
        <div className="flex items-start gap-3 rounded-md border border-violet-200 bg-violet-50 p-3" data-testid="pause-billing-callout">
          <PauseCircle className="mt-0.5 h-5 w-5 shrink-0 text-violet-600" />
          <p className="text-sm text-violet-800"><span className="font-semibold">Subscription pauses when your books are closed</span>, so you only pay when you are looking to take on new patients.</p>
        </div>
        <div className="flex items-start gap-3 rounded-md border border-sky-200 bg-sky-50 p-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-sky-600" />
          <p className="text-sm text-sky-800">All clinicians are <span className="font-semibold">AHPRA-verified</span> before appearing in the GP directory.</p>
        </div>
      </div>
      <div className="mb-5">
        <DemoVideo src="/videos/clinic.mp4" title="How clinic managers use SimplyRefer" buttonLabel="See how it works (60s)" className="w-full" testId="clinic-signup-demo" />
      </div>
      <form onSubmit={handleSubmit} className="space-y-5" data-testid="clinic-signup-form">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2"><Label htmlFor="firstName">First name</Label><Input id="firstName" required value={form.firstName} onChange={set("firstName")} placeholder="First name" data-testid="clinic-signup-firstname" /></div>
          <div className="space-y-2"><Label htmlFor="lastName">Last name</Label><Input id="lastName" required value={form.lastName} onChange={set("lastName")} placeholder="Last name" data-testid="clinic-signup-lastname" /></div>
        </div>
        <div className="space-y-2"><Label htmlFor="clinicName">Clinic name</Label><Input id="clinicName" required value={form.clinicName} onChange={set("clinicName")} placeholder="Your clinic name" data-testid="clinic-signup-clinicname" /></div>
        <div className="space-y-2"><Label htmlFor="suburb">Suburb</Label><Input id="suburb" required value={form.suburb} onChange={set("suburb")} placeholder="Suburb" data-testid="clinic-signup-suburb" /></div>
        <div className="space-y-2"><Label htmlFor="email">Account billing email</Label><Input id="email" type="email" required value={form.email} onChange={set("email")} placeholder="admin@example.com" data-testid="clinic-signup-email" /></div>
        <div className="space-y-2"><Label htmlFor="password">Password</Label><PasswordInput id="password" required value={form.password} onChange={set("password")} placeholder="At least 6 characters" testId="clinic-signup-password" /></div>
        <div className="space-y-2"><Label htmlFor="confirmPassword">Confirm password</Label><PasswordInput id="confirmPassword" required value={form.confirmPassword} onChange={set("confirmPassword")} placeholder="Re-enter your password" testId="clinic-signup-confirm-password" /></div>
        {error && (
          <div className="space-y-2 text-sm text-destructive" data-testid="clinic-signup-error">
            <p>{error}</p>
            {emailConflict && (
              <p><Link className="underline" to="/login">Log in to your existing account</Link>, then set it up as a clinic with multiple clinicians from your account.</p>
            )}
          </div>
        )}
        <Button type="submit" className="w-full" disabled={loading} data-testid="clinic-signup-submit">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Start free trial
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-500">
        Already registered?{" "}
        <Link to="/login" className="font-medium text-primary hover:underline" data-testid="go-to-clinic-login">Sign in</Link>
      </p>
    </AuthLayout>
  );
}
